/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { getPayloadObject, getTelemetryAttributes, ITelemetryContent, TelemetryConstant } from '@msdyn365-commerce-modules/utilities';
import { ICoreContext } from '@msdyn365-commerce/core';
import { CartLine } from '@msdyn365-commerce/retail-proxy';
import React, { useState } from 'react';
import { formatDate, getDatePlaceholder } from '../../../utilities/format-date';

export interface ICheckoutDeliveryDateProps {
    className?: string;
    isDisabled?: boolean;
    minRangeValue?: string;
    maxRangeValue?: string;
    validationErrorForRange?: string;
    fallbackPattern?: string;
    ariaLabel?: string;
    telemetryContent?: ITelemetryContent;
    disclaimerText?: string;
    heading?: string;
    checkBoxLabel?: string;
    onChangeMinValue?: void;
    id?: string;
    cartLines?: CartLine[];
    onChange(event: React.ChangeEvent<HTMLInputElement>): void;
    isDateEnabled: boolean;
    selectedDate: string;
    context: ICoreContext;
}

const CheckoutDeliveryDate: React.FC<ICheckoutDeliveryDateProps> = ({
    className,
    isDisabled,
    fallbackPattern,
    ariaLabel,
    telemetryContent,
    onChangeMinValue,
    checkBoxLabel,
    disclaimerText,
    heading,
    onChange,
    isDateEnabled,
    selectedDate,
    context
}) => {
    const [selectedDate1, setSelectedDate] = useState(selectedDate);
    const [isDateEnabled1, setIsDateEnabled] = useState(isDateEnabled);
    // State to store today's date
    const [minDate, setMinDate] = useState('');

    // Update minDate state with today's date on component mount
    useState(() => {
        const today = new Date().toISOString().split('T')[0];
        setMinDate(today);
    });

    const handleDateChange = (event: React.FocusEvent<HTMLInputElement>) => {
        const selectedValue = event.target.value;
        // Check if the selected date is allowed
        if (selectedValue >= minDate) {
            setSelectedDate(selectedValue);
            onChange(event);
        }
    };

    const handleCheckboxChange = () => {
        setIsDateEnabled(prev => {
            const newIsDateEnabled = !prev;
            if (!newIsDateEnabled) {
                // Clear the date and trigger onChange event with empty value
                setSelectedDate('');
                const event = {
                    target: {
                        name: 'dateInput',
                        value: ''
                    }
                } as React.ChangeEvent<HTMLInputElement>;
                onChange(event);
            }
            return newIsDateEnabled;
        });
    };

    const payLoad = getPayloadObject('click', telemetryContent!, TelemetryConstant.DeliveryOptions);
    const attributes = getTelemetryAttributes(telemetryContent!, payLoad);

    return (
        <div className={className}>
            <div className='checkBoxLabelSection'>
                <span className={`${className}__lbl`}>{checkBoxLabel}</span>
            </div>
            <div className='checkBoxInputSection'>
                <input
                    className={`${className}__checkbox`}
                    type='checkbox'
                    aria-label={ariaLabel}
                    name='requestDeliveryDate'
                    checked={isDateEnabled1}
                    onChange={handleCheckboxChange}
                />
                <span className={`${className}__disclaimer`}>{disclaimerText}</span>
            </div>

            {isDateEnabled1 && (
                <div className={`${className}__calender`}>
                    <span className={`${className}__calender-h`}>{heading}</span>
                    <input
                        className={`${className}__calender-c ${selectedDate1 ? 'has-value' : ''}`}
                        type='date'
                        name='dateInput'
                        value={selectedDate1}
                        onChange={handleDateChange}
                        min={minDate}
                        pattern={fallbackPattern}
                        {...attributes}
                        disabled={isDisabled}
                    />
                    <span className={`${className}__calender-formatted-date`}>
                        {selectedDate1 ? formatDate(context, new Date(`${selectedDate1}T00:00:00`)) : getDatePlaceholder(context)}
                    </span>
                </div>
            )}
        </div>
    );
};

export default CheckoutDeliveryDate;
